import restClient from "api/restClient";
import { Button } from "components/Button/Button";
import { Modal } from "components/Modal/Modal";
import NotificationService from "components/Notification/Notification.service";
import RestErrorNotification from "components/Notification/RestErrorNotification";
import SimpleNotification from "components/Notification/SimpleNotification";
import { useCallback, useState } from "react";
import { HookType, StampResult } from "./hookInputSchemaStamp.helpers";
import "./HookInputSchemaStamp.scss";

const formatSchema = (schema: Record<string, unknown> | null | undefined): string =>
    schema && Object.keys(schema).length ? JSON.stringify(schema, null, 2) : "(empty)";

interface HookStampButtonProps {
    hookType: HookType;
    /** The saved object template id (stamping operates on the saved record). */
    objectTemplateId?: string;
    /** Enable the button only when a flow is bound to this hook. */
    flowSelected: boolean;
    /** Follow the field's read-only/disabled state. */
    disabled?: boolean;
    /** Called after a successful stamp so the caller can refetch the record. */
    fetchResourceData?: (() => any) | null;
}

/**
 * The one-click "Stamp input schema" button that sits next to a lifecycle-hook
 * flow picker (on_create / on_update / on_delete) on the object template screen.
 * Clicking runs a dry run (`apply=false`); if the predefined schema would change
 * the current one it opens a confirm dialog showing current-vs-new, and only then
 * writes it (`apply=true`). See operator-task-ot-lifecycle-hook-one-click-input-schema.
 */
export const HookStampButton = ({
    hookType,
    objectTemplateId,
    flowSelected,
    disabled,
    fetchResourceData,
}: HookStampButtonProps) => {
    const [loading, setLoading] = useState(false);
    const [confirm, setConfirm] = useState<StampResult | null>(null);
    const [isApplying, setIsApplying] = useState(false);

    const handleStampClick = useCallback(() => {
        if (!objectTemplateId) {
            return;
        }
        setLoading(true);
        restClient
            .post(`/object_template/${objectTemplateId}/stamp_hook_input_schema?by=id`, {
                hook_type: hookType,
                apply: false,
            })
            .then((response) => {
                const result: StampResult = response?.data;
                if (!result?.changed) {
                    NotificationService.info(
                        <SimpleNotification
                            title="Input schema already up to date"
                            subtitle={`The ${hookType} hook flow already has the correct input schema.`}
                        />
                    );
                    return;
                }
                setConfirm(result);
            })
            .catch((error) => {
                NotificationService.error(
                    <RestErrorNotification
                        titleOverride="Could not compute the hook input schema"
                        error={error}
                    />
                );
            })
            .finally(() => setLoading(false));
    }, [objectTemplateId, hookType]);

    const handleConfirmApply = useCallback(() => {
        if (!objectTemplateId || !confirm) {
            return;
        }
        setIsApplying(true);
        restClient
            .post(`/object_template/${objectTemplateId}/stamp_hook_input_schema?by=id`, {
                hook_type: confirm.hook_type,
                apply: true,
            })
            .then(() => {
                NotificationService.success(
                    <SimpleNotification
                        title="Input schema stamped"
                        subtitle={`The correct ${confirm.hook_type} input schema was written onto flow "${confirm.flow_name}".`}
                    />
                );
                setConfirm(null);
                fetchResourceData?.();
            })
            .catch((error) => {
                NotificationService.error(
                    <RestErrorNotification
                        titleOverride="Could not stamp the hook input schema"
                        error={error}
                    />
                );
            })
            .finally(() => setIsApplying(false));
    }, [objectTemplateId, confirm, fetchResourceData]);

    return (
        <>
            <Button
                size="small"
                variation="dark"
                label="Stamp input schema"
                onClick={handleStampClick}
                loading={loading}
                disabled={!flowSelected || !!disabled || loading}
            />
            <Modal
                isVisible={!!confirm}
                onCancel={() => (isApplying ? undefined : setConfirm(null))}
                title="Overwrite the hook flow input schema?"
                width="640px"
                closable={false}
                destroyOnClose
            >
                {confirm && (
                    <div className="hookInputSchemaStamp__confirm">
                        <p className="hookInputSchemaStamp__confirmText">
                            This overwrites the current input schema of the {confirm.hook_type} hook
                            flow <strong>{confirm.flow_name}</strong> with the predefined schema for
                            this hook. You can still edit it afterwards.
                        </p>
                        <div className="hookInputSchemaStamp__diff">
                            <div className="hookInputSchemaStamp__diffPane">
                                <div className="hookInputSchemaStamp__diffLabel">Current</div>
                                <pre className="hookInputSchemaStamp__code">
                                    {formatSchema(confirm.current_input_schema)}
                                </pre>
                            </div>
                            <div className="hookInputSchemaStamp__diffPane">
                                <div className="hookInputSchemaStamp__diffLabel">New</div>
                                <pre className="hookInputSchemaStamp__code">
                                    {formatSchema(confirm.computed_input_schema)}
                                </pre>
                            </div>
                        </div>
                        {/*
                          Canonical modal button row (ui-design-principle-modals-dialogs
                          reference impl): Cancel on the LEFT as the neutral white button,
                          primary-blue confirm on the RIGHT. No corner close "×".
                        */}
                        <div className="hookInputSchemaStamp__actions">
                            <Button
                                label="Cancel"
                                variation="white"
                                onClick={() => setConfirm(null)}
                                disabled={isApplying}
                            />
                            <Button
                                label="Stamp input schema"
                                variation="primary"
                                onClick={handleConfirmApply}
                                loading={isApplying}
                                disabled={isApplying}
                            />
                        </div>
                    </div>
                )}
            </Modal>
        </>
    );
};
